import type { MatDiscoveryItem } from '../../../types/mat-types';
import type { TransformedMATRow, PaginationSlice } from './useMatDataSource.types';

const DISCOVERED_ROW_MARKER = '__isDiscovered__';

// Transforms MatDiscoveryItem array into grid row format
export function transformDiscoveredData(discoveryItems: MatDiscoveryItem[]): TransformedMATRow[] {
  return discoveryItems.map((item) => {
    const firstRow = item.discovered_rows?.[0]?.data || {};
    return {
      ...firstRow,
      MatKey: item.mat_key || firstRow.MatKey,
      MatRowIndex: item.mat_id,
      DataSourceStatus: item.data_source_status,
      [DISCOVERED_ROW_MARKER]: true,
    } as TransformedMATRow;
  });
}

/**
 * Calculates which discovered rows and MAT API parameters are needed for a given pagination request
 * Combines discovered data with server-paginated MAT data
 */
export function calculatePaginationSlice(
  offset: number,
  limit: number,
  discoveredRows: TransformedMATRow[],
  includeDiscovered: boolean
): PaginationSlice {
  const discoveredCount = includeDiscovered ? discoveredRows.length : 0;

  // If no discovered data, fetch directly from MAT API
  if (discoveredCount === 0) {
    return {
      discoveredSlice: [],
      matOffset: offset,
      matLimit: limit,
    };
  }

  // If we're still within the discovered rows range
  if (offset < discoveredCount) {
    const endIndex = Math.min(offset + limit, discoveredCount);
    const discoveredSlice = discoveredRows.slice(offset, endIndex);
    const remainingLimit = limit - discoveredSlice.length;

    return {
      discoveredSlice,
      matOffset: 0,
      matLimit: Math.max(0, remainingLimit),
    };
  }

  // We're past the discovered rows, adjust offset for MAT API
  return {
    discoveredSlice: [],
    matOffset: offset - discoveredCount,
    matLimit: limit,
  };
}
